import { useNavigate } from 'react-router-dom'

import { Box, Button, Table, Text } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import Loading from 'modules/core/components/Loading'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import hasFeature from 'modules/core/lib/hasFeature'
import i18n from 'modules/core/lib/i18n'

import useIsTimeFormat24H from 'modules/shift_management/hooks/useIsTimeFormat24H'
import { trackAddDefaultShiftStart } from 'modules/shift_management/lib/trackEvents'
import defaultShiftsCollection from 'modules/shift_management/store/api/collections/defaultShifts'
import DefaultShifts from 'modules/shift_management/store/internal/collections/DefaultShifts'
import { useGetPlannedBreaks } from 'modules/time_planning/hooks/plannedBreaks/useGetPlannedBreaks'

import SavedShift from './SavedShift'
import { Sticky } from './utils'

const SavedShiftsSettings = () => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()

  const { isTimeFormat24H } = useIsTimeFormat24H()

  const hoursMinWidth = isTimeFormat24H ? 's200' : 's240'

  const { loading } = useAsyncData(async () => {
    await defaultShiftsCollection.fetch<DefaultShifts>()
  }, [])

  const onClickNew = () => {
    trackAddDefaultShiftStart({ daysPreselected: false, from: 'settings' })
    navigate(navigation.shifts.savedShifts.newSavedShift.fullPath())
  }

  // The same flag is used to indicate extras like location, work area and notes
  const hasColors = hasFeature('shift_management_colours_for_shifts')
  const hasBreaks = hasFeature('time_settings_breaks')

  const defaultShifts = DefaultShifts.buildFromProps(
    defaultShiftsCollection.map((shift) => shift.attributes)
  )

  const defaultShiftIds = defaultShifts.active.map((ds) => ds.id)
  const { plannedBreaksCollection } = useGetPlannedBreaks({
    variables: { defaultShiftIds },
    skip: !defaultShiftIds.length,
  })

  if (loading) {
    return <Loading />
  }

  const activeDefaultShifts = defaultShifts.active
  const shiftsToShow = activeDefaultShifts.length !== 0

  const firstCellSticky: Sticky = {
    position: 'left',
  }

  const lastCellSticky: Sticky = {
    position: 'right',
  }

  return (
    <>
      <Box gap="s16">
        <Box justifyContent="spaceBetween" flexDirection="row">
          <Text.Header2>{i18n.t('shift_management.saved_shifts.your_saved_shifts')}</Text.Header2>
          <Button type="secondary" onClick={onClickNew} iconName="add">
            {i18n.t('shift_management.default_shift.btn_new_shift_2')}
          </Button>
        </Box>
        <Box width="full">
          <Table autoWidth overflowX="scroll">
            <Table.Head>
              <Table.Row>
                <Table.HeadCell sticky={shiftsToShow ? firstCellSticky : undefined}>
                  <Box minWidth={shiftsToShow ? 's200' : 's140'}>
                    <Text.Normal>
                      {i18n.t('shift_management.saved_shifts.attributes.shift_name')}
                    </Text.Normal>
                  </Box>
                </Table.HeadCell>
                <Table.HeadCell>
                  <Box minWidth={hoursMinWidth}>
                    <Text.Normal>
                      {i18n.t('shift_management.saved_shifts.attributes.hour_range')}
                    </Text.Normal>
                  </Box>
                </Table.HeadCell>
                {hasBreaks && (
                  <Table.HeadCell>
                    <Box minWidth="s140">
                      <Text.Normal>
                        {i18n.t('shift_management.saved_shifts.attributes.break')}
                      </Text.Normal>
                    </Box>
                  </Table.HeadCell>
                )}
                <Table.HeadCell>
                  <Box minWidth="s140">
                    {hasColors && (
                      <Text.Normal>
                        {i18n.t('shift_management.shift_details_modal.workplace')}
                      </Text.Normal>
                    )}
                  </Box>
                </Table.HeadCell>
                <Table.HeadCell>
                  <Box minWidth="s140">
                    {hasColors && (
                      <Text.Normal>
                        {i18n.t('shift_management.shift_details_modal.workarea')}
                      </Text.Normal>
                    )}
                  </Box>
                </Table.HeadCell>
                <Table.HeadCell>
                  <Box minWidth="s140">
                    <Text.Normal>
                      {i18n.t('shift_management.saved_shifts.form_access.title')}
                    </Text.Normal>
                  </Box>
                </Table.HeadCell>
                {shiftsToShow && <Table.HeadCell width={80} sticky={lastCellSticky} />}
              </Table.Row>
            </Table.Head>
            <Table.Body>
              {!shiftsToShow ? (
                <Table.Blank
                  colSpan={6}
                  text={i18n.t('shift_management.saved_shifts.empty')}
                  ctaButtonText={i18n.t('shift_management.default_shift.btn_new_shift_2')}
                  ctaButtonOnClick={onClickNew}
                />
              ) : (
                activeDefaultShifts.map((defaultShift) => (
                  <SavedShift
                    key={defaultShift.id}
                    defaultShift={defaultShift}
                    plannedBreaks={plannedBreaksCollection.active.filterByDefaultShift(
                      defaultShift.id
                    )}
                    hoursMinWidth={hoursMinWidth}
                  />
                ))
              )}
            </Table.Body>
          </Table>
        </Box>
      </Box>
    </>
  )
}

export default observer(SavedShiftsSettings)
